import { SafeParseError, SafeParseReturnType, ZodIssue, ZodSchema } from "zod";

export class FoundryError extends Error {
  message: string;
  error_code: string;
}

export class FoundryZodParseError extends FoundryError {
  constructor(zodIssue: ZodIssue) {
    super();
    this.error_code = zodIssue.code;
    this.message = `${zodIssue.path}: ${zodIssue.message}`;
  }

  static validate<TOutput>(
    schema: ZodSchema<TOutput>,
    data: any
  ): { data: TOutput | null; errors: FoundryZodParseError[] } {
    const parseResult = schema.safeParse(data);

    if (parseResult.success) {
      return { data: parseResult.data, errors: [] };
    }

    return {
      data: null,
      errors: parseResult.error.errors.map((e) => new FoundryZodParseError(e)),
    };
  }
}

export class FoundryApiError extends FoundryError {
  message: string;
  error_code: string;
  productId: string;
}

export enum FoundryApiErrorCode {
  ProductDoesNotExist = "product_does_not_exist",
  ProductNotAvailable = "product_not_available",
}

export class ProductDoesNotExistError extends FoundryApiError {
  constructor(productId: string) {
    super();
    this.message = "Product does not exist";
    this.error_code = FoundryApiErrorCode.ProductDoesNotExist;
    this.productId = productId;
  }
}

export class ProductNotAvailableError extends FoundryApiError {
  constructor(
    productId: string,
    productName: string,
    availableQuantity: number
  ) {
    super();

    if (availableQuantity === 0) {
      this.message = `No copies of "${productName}" are available.`;
    } else if (availableQuantity === 1) {
      this.message = `Only one copy of "${productName}" is available.`;
    } else {
      this.message = `Only ${availableQuantity} copies of "${productName}" are available.`;
    }
    this.error_code = FoundryApiErrorCode.ProductNotAvailable;
    this.productId = productId;
  }
}
